import { useClientPortalAPI } from "@certn/api";
import {
  AvatarWithName,
  ColumnDefinition,
  DangerTag,
  DataTable,
  GhostIconButton,
  Link,
  Stack,
  SuccessTag,
  Text,
  mq,
} from "@certn/cascadia-react";
import {
  sortDescriptorToOrderingParam,
  orderingParamToSortDescriptor,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDateTime, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { CreateWebhookModal } from "./CreateWebhookModal/Modal";
import { Empty } from "./Empty";
import { formatWebhookSecret } from "./utils";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { Subtitle } from "../../Subtitle";

export function WebhookList() {
  const { t } = useTranslation();
  const { apiConfigurationID = "" } = useParams();
  const { params, setParam } = useQueryParams();

  const ordering = params.ordering || "-last_verified";

  const { data, isLoading, error, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/public-api/webhooks/",
    {
      public_api_configuration: apiConfigurationID,
      page_size: 100, // Note: no api config can have more than 100 webhooks
      ...params,
    }
  );

  const rows = data?.results || [];

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "webhook_url",
      name: t("Webhook URL"),
      isRowHeader: true,
      render(item) {
        return <Text className={styles.noWrapMobile}>{item.webhook_url}</Text>;
      },
    },
    {
      key: "secret",
      name: t("Secret string"),
      render(item) {
        return (
          <Stack orientation="horizontal" gap="space-0">
            <Text className={styles.noWrapMobile}>
              {formatWebhookSecret(item.secret)}
            </Text>
            <GhostIconButton
              icon="content_copy"
              size="small"
              aria-label={t("Copy secret")}
              onPress={() => navigator.clipboard.writeText(item.secret)}
            />
          </Stack>
        );
      },
    },
    {
      key: "status",
      name: t("Status"),
      render(item) {
        return (
          <Text className={styles.noWrapMobile}>
            {item.is_active ? (
              <SuccessTag type="subtle">{t("Active")}</SuccessTag>
            ) : (
              <DangerTag type="subtle">{t("Inactive")}</DangerTag>
            )}
          </Text>
        );
      },
    },
    {
      key: "last_verified",
      name: t("Last verified"),
      allowsSorting: true,
      render(item) {
        return (
          <Text>
            {formatDateTime(item.last_verified, "MMM d, yyyy 'at' h:mma")}
          </Text>
        );
      },
    },
    {
      key: "created_by",
      name: t("Created by"),
      render(item) {
        return (
          <AvatarWithName
            name={parseName(item.created_by?.auth0_full_name)}
            subtitle={item.created_by?.auth0_email}
          />
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      render(item) {
        return <ActionsDropdown webhook={item} onItemChanged={mutate} />;
      },
      width: 48,
    },
  ];

  return (
    <Stack gap="space-6">
      <Subtitle
        subtitle={t("Webhooks")}
        description={
          <Trans>
            Webhooks are a way for us to send you updates with your account in
            realtime, such as status updates to cases and checks.{" "}
            <Link
              testID="developer-api-learn-more"
              href={`/settings/integrations/${apiConfigurationID}/documentation#webhooks`}
              linkStyle="monochrome"
            >
              Learn more
            </Link>
          </Trans>
        }
      >
        <CreateWebhookModal
          apiConfigurationID={apiConfigurationID}
          onSuccess={mutate}
        />
      </Subtitle>

      {error || isLoading || rows.length > 0 ? (
        <DataTable
          isLoading={isLoading}
          testId="webhooks-table"
          columns={columns}
          rows={rows}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          renderEmptyState={() =>
            error && (
              <Text color="text-danger">
                {t("Something went wrong when loading webhooks.")}
              </Text>
            )
          }
          sortDescriptor={orderingParamToSortDescriptor(ordering)}
          aria-label="webhooks-table"
        />
      ) : (
        <Empty
          apiConfigurationID={apiConfigurationID}
          onWebhookCreated={mutate}
        />
      )}
    </Stack>
  );
}

const styles = {
  noWrapMobile: css(
    mq({
      whiteSpace: ["nowrap", "normal"],
    })
  ),
};
